import React, { useEffect, useState, useCallback } from "react";

const API = "/api/omnivoice";
async function apiFetch(path: string, opts?: RequestInit) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const STATUS_COLOR: Record<string, string> = {
  pending: "#64748b",
  transcribed: "#3b82f6",
  cleaned: "#8b5cf6",
  translated: "#f59e0b",
  synthesized: "#06b6d4",
  delivered: "#22c55e",
  failed: "#ef4444",
};

const LANG_FLAGS: Record<string, string> = {
  ar: "🇦🇪", en: "🇬🇧", fa: "🇮🇷", ur: "🇵🇰",
  hi: "🇮🇳", tr: "🇹🇷", ru: "🇷🇺", fr: "🇫🇷",
  de: "🇩🇪", zh: "🇨🇳", es: "🇪🇸", pt: "🇧🇷",
};

const flag = (lang: string) => LANG_FLAGS[(lang || "").toLowerCase().slice(0, 2)] || "🌐";

const latencyColor = (ms: number | null) => {
  if (!ms) return "#64748b";
  if (ms < 3000) return "#22c55e";
  if (ms < 6000) return "#eab308";
  return "#ef4444";
};

const card: React.CSSProperties = {
  background: "rgba(19,27,46,0.7)",
  backdropFilter: "blur(14px)",
  border: "1px solid rgba(255,214,165,0.1)",
  borderRadius: 12,
  padding: "16px 20px",
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  background: active ? "rgba(255,214,165,0.15)" : "transparent",
  color: active ? "#ffd6a5" : "#64748b",
  transition: "all 0.2s",
});

// ── Asset Card ───────────────────────────────────────────────────────────────
function AssetCard({ asset, onClick }: { asset: any; onClick: () => void }) {
  const color = STATUS_COLOR[asset.status] || "#64748b";
  return (
    <div
      onClick={onClick}
      style={{
        ...card,
        borderLeft: `3px solid ${color}`,
        marginBottom: 10,
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace" }}>
            {asset.source}
          </span>
          <span style={{ fontSize: 11, color: "#64748b" }}>·</span>
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {asset.content_type === "voice_note" ? "🎙️ voice" : "💬 text"}
          </span>
          {asset.group_name && (
            <>
              <span style={{ fontSize: 11, color: "#64748b" }}>·</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>📍 {asset.group_name}</span>
            </>
          )}
        </div>
        <div style={{ fontSize: 13, color: "#e2e8f0", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {asset.cleaned_text || asset.raw_text || asset.transcript || "(no text)"}
        </div>
        <div style={{ display: "flex", gap: 10, fontSize: 11, color: "#64748b" }}>
          <span>From: +{asset.sender_phone}</span>
          <span>→</span>
          <span>To: +{asset.recipient_phone}</span>
          {asset.detected_language && <span>{flag(asset.detected_language)} {asset.detected_language?.toUpperCase()}</span>}
          {asset.target_language && <><span>→</span><span>{flag(asset.target_language)} {asset.target_language?.toUpperCase()}</span></>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <span style={{ fontSize: 11, color, fontWeight: 700, textTransform: "uppercase" }}>{asset.status}</span>
        {asset.pipeline_ms != null && (
          <span style={{ fontSize: 11, color: latencyColor(asset.pipeline_ms), fontWeight: 600 }}>
            {asset.pipeline_ms}ms
          </span>
        )}
        <span style={{ fontSize: 10, color: "#475569" }}>
          {asset.created_at ? new Date(asset.created_at).toLocaleTimeString() : ""}
        </span>
      </div>
    </div>
  );
}

// ── Asset Modal ───────────────────────────────────────────────────────────────
function AssetModal({ asset, onClose, onRedeliver }: { asset: any; onClose: () => void; onRedeliver: (id: string) => void }) {
  const layers = [
    { label: "1. Transcript", value: asset.transcript, lang: asset.detected_language },
    { label: "2. Cleaned", value: asset.cleaned_text, lang: asset.detected_language },
    { label: "3. Translated", value: asset.translated_text, lang: asset.target_language },
    { label: "4. Voice", value: asset.normalized_audio_url ? "✅ Synthesized" : "—", lang: null },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        ...card,
        width: "min(700px, 95vw)",
        maxHeight: "85vh",
        overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ color: "#ffd6a5", margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>
            Message Asset
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 20, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16, fontSize: 12 }}>
          {[
            ["ID", asset.id?.slice(0, 16) + "…"],
            ["Source", asset.source],
            ["Type", asset.content_type],
            ["Status", asset.status],
            ["From", `+${asset.sender_phone}`],
            ["To", `+${asset.recipient_phone}`],
            ["Group", asset.group_name || "—"],
            ["Pipeline", asset.pipeline_ms ? `${asset.pipeline_ms}ms` : "—"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 6 }}>
              <span style={{ color: "#64748b" }}>{k}:</span>
              <span style={{ color: "#e2e8f0" }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {layers.map(l => (
            <div key={l.label} style={{
              background: "rgba(255,255,255,0.04)",
              borderRadius: 8, padding: "10px 14px",
            }}>
              <div style={{ fontSize: 11, color: "#ffd6a5", fontWeight: 700, marginBottom: 6 }}>
                {l.label} {l.lang && <>{flag(l.lang)} {l.lang?.toUpperCase()}</>}
              </div>
              <div style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.5 }}>
                {l.value || <span style={{ color: "#475569" }}>—</span>}
              </div>
            </div>
          ))}
        </div>

        {asset.normalized_audio_url && (
          <div style={{ marginTop: 12 }}>
            <audio controls src={asset.normalized_audio_url} style={{ width: "100%" }} />
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button
            onClick={() => onRedeliver(asset.id)}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,214,165,0.2)",
              background: "rgba(255,214,165,0.1)", color: "#ffd6a5", cursor: "pointer",
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 13,
            }}
          >
            ↩ Redeliver
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab 1: Live Feed ──────────────────────────────────────────────────────────
function LiveFeed() {
  const [assets, setAssets] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/assets?limit=30");
      setAssets(d.assets || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  const redeliver = async (id: string) => {
    try {
      await apiFetch(`/assets/${id}/redeliver`, { method: "POST" });
      setSelected(null);
      load();
    } catch (_) {}
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ color: "#ffd6a5", margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>Live Feed</h3>
        <button onClick={load} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(255,214,165,0.2)", background: "transparent", color: "#ffd6a5", cursor: "pointer", fontSize: 12 }}>
          ↻ Refresh
        </button>
      </div>
      {loading ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>Loading…</div>
      ) : assets.length === 0 ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>No assets yet.</div>
      ) : (
        assets.map(a => <AssetCard key={a.id} asset={a} onClick={() => setSelected(a)} />)
      )}
      {selected && <AssetModal asset={selected} onClose={() => setSelected(null)} onRedeliver={redeliver} />}
    </div>
  );
}

// ── Tab 2: Message Assets ─────────────────────────────────────────────────────
function MessageAssets() {
  const [assets, setAssets] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/assets?limit=100").then(d => { setAssets(d.assets || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = assets.filter(a =>
    !search || [a.sender_phone, a.recipient_phone, a.group_name, a.cleaned_text, a.raw_text]
      .some(v => (v || "").toLowerCase().includes(search.toLowerCase()))
  );

  const redeliver = async (id: string) => {
    try {
      await apiFetch(`/assets/${id}/redeliver`, { method: "POST" });
      setSelected(null);
    } catch (_) {}
  };

  return (
    <div>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by phone, group, text…"
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 8, marginBottom: 16,
          background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)",
          color: "#e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box",
        }}
      />
      {loading ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>No results.</div>
      ) : (
        filtered.map(a => <AssetCard key={a.id} asset={a} onClick={() => setSelected(a)} />)
      )}
      {selected && <AssetModal asset={selected} onClose={() => setSelected(null)} onRedeliver={redeliver} />}
    </div>
  );
}

// ── Tab 3: Groups ─────────────────────────────────────────────────────────────
function Groups() {
  const [groups, setGroups] = useState<any[]>([]);
  const [form, setForm] = useState({ group_id: "", group_name: "", registered_by: "dashboard" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => apiFetch("/groups").then(d => { setGroups(d.groups || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!form.group_id) return;
    setSubmitting(true);
    try {
      await apiFetch("/groups/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setForm({ group_id: "", group_name: "", registered_by: "dashboard" });
      load();
    } catch (_) {} finally { setSubmitting(false); }
  };

  const remove = async (groupId: string) => {
    try {
      await apiFetch(`/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
      load();
    } catch (_) {}
  };

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif" }}>Register Group</h4>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["group_id", "group_name"].map(f => (
            <input
              key={f}
              placeholder={f === "group_id" ? "Group ID (e.g. 971...@g.us)" : "Display name"}
              value={(form as any)[f]}
              onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
              style={{
                flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6,
                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)",
                color: "#e2e8f0", fontSize: 13, outline: "none",
              }}
            />
          ))}
          <button
            onClick={register}
            disabled={submitting || !form.group_id}
            style={{
              padding: "8px 16px", borderRadius: 6, border: "none",
              background: submitting ? "#334155" : "#ffd6a5", color: "#0b1326",
              fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontSize: 13,
            }}
          >
            {submitting ? "…" : "Register"}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>Loading…</div>
      ) : groups.length === 0 ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>No groups registered yet.</div>
      ) : groups.map(g => (
        <div key={g.id} style={{ ...card, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: 14 }}>{g.group_name || g.group_id}</div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{g.group_id}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              By: {g.registered_by} · {g.registered_at ? new Date(g.registered_at).toLocaleDateString() : ""}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: g.is_active ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
              {g.is_active ? "ACTIVE" : "INACTIVE"}
            </span>
            {g.is_active && (
              <button
                onClick={() => remove(g.group_id)}
                style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11 }}
              >
                Deactivate
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Tab 4: Sessions & Profiles ────────────────────────────────────────────────
function SessionsProfiles() {
  const [phone, setPhone] = useState("");
  const [profile, setProfile] = useState<any>(null);
  const [edit, setEdit] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!phone) return;
    try {
      const p = await apiFetch(`/profile/${phone}`);
      setProfile(p);
      setEdit({ preferred_language: p.preferred_language, preferred_format: p.preferred_format });
    } catch (_) {}
  };

  const save = async () => {
    setSaving(true);
    try {
      const p = await apiFetch(`/profile/${phone}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(edit),
      });
      setProfile(p);
    } catch (_) {} finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif" }}>Lookup / Edit Profile</h4>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="Phone (e.g. 971501234567)"
            style={{
              flex: 1, padding: "8px 12px", borderRadius: 6,
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)",
              color: "#e2e8f0", fontSize: 13, outline: "none",
            }}
          />
          <button onClick={load} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#ffd6a5", color: "#0b1326", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            Load
          </button>
        </div>
      </div>

      {profile && (
        <div style={card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, fontSize: 13 }}>
            {[
              ["Phone", profile.phone],
              ["Display Name", profile.display_name || "—"],
              ["Assets Sent", profile.total_assets_sent],
              ["Assets Received", profile.total_assets_received],
              ["Voice Cloned", profile.is_voice_cloned ? "Yes" : "No"],
              ["Last Active", profile.last_active_at ? new Date(profile.last_active_at).toLocaleDateString() : "—"],
            ].map(([k, v]) => (
              <div key={k as string}>
                <div style={{ color: "#64748b", fontSize: 11, marginBottom: 2 }}>{k}</div>
                <div style={{ color: "#e2e8f0" }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Language</div>
              <select
                value={edit.preferred_language || "ar"}
                onChange={e => setEdit((p: any) => ({ ...p, preferred_language: e.target.value }))}
                style={{ padding: "6px 10px", borderRadius: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)", color: "#e2e8f0", fontSize: 13 }}
              >
                {["ar", "en", "fa", "ur", "hi", "tr", "ru", "fr", "de", "zh", "es", "pt"].map(l => (
                  <option key={l} value={l}>{flag(l)} {l.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Format</div>
              <select
                value={edit.preferred_format || "both"}
                onChange={e => setEdit((p: any) => ({ ...p, preferred_format: e.target.value }))}
                style={{ padding: "6px 10px", borderRadius: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)", color: "#e2e8f0", fontSize: 13 }}
              >
                <option value="voice">🎙️ Voice only</option>
                <option value="text">💬 Text only</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={save}
                disabled={saving}
                style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: saving ? "#334155" : "#ffd6a5", color: "#0b1326", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: 13 }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 5: Analytics ──────────────────────────────────────────────────────────
function Analytics() {
  const [stats, setStats] = useState<any>(null);
  const [langStats, setLangStats] = useState<any>(null);
  const [latency, setLatency] = useState<any>(null);

  useEffect(() => {
    Promise.all([
      apiFetch("/stats").then(setStats).catch(() => {}),
      apiFetch("/stats/languages").then(setLangStats).catch(() => {}),
      apiFetch("/stats/latency").then(setLatency).catch(() => {}),
    ]);
  }, []);

  const statCards = stats ? [
    { label: "Total Assets", value: stats.total_assets },
    { label: "Delivered", value: stats.delivered, color: "#22c55e" },
    { label: "Failed", value: stats.failed, color: "#ef4444" },
    { label: "Success Rate", value: `${stats.success_rate}%`, color: "#ffd6a5" },
    { label: "Avg Pipeline", value: stats.avg_pipeline_ms ? `${stats.avg_pipeline_ms}ms` : "—", color: latencyColor(stats.avg_pipeline_ms) },
  ] : [];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ ...card, textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color || "#e2e8f0", fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {latency && (
        <div style={{ ...card, marginBottom: 16 }}>
          <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14 }}>Pipeline Latency</h4>
          <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
            <span style={{ color: "#64748b" }}>Min: <span style={{ color: latencyColor(latency.min_ms) }}>{latency.min_ms ?? "—"}ms</span></span>
            <span style={{ color: "#64748b" }}>Avg: <span style={{ color: latencyColor(latency.avg_ms) }}>{latency.avg_ms ?? "—"}ms</span></span>
            <span style={{ color: "#64748b" }}>Max: <span style={{ color: latencyColor(latency.max_ms) }}>{latency.max_ms ?? "—"}ms</span></span>
          </div>
        </div>
      )}

      {langStats && langStats.pairs?.length > 0 && (
        <div style={card}>
          <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14 }}>Language Pairs</h4>
          {langStats.pairs.slice(0, 10).map((p: any) => (
            <div key={`${p.src}-${p.tgt}`} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <span style={{ color: "#e2e8f0" }}>{flag(p.src)} {p.src?.toUpperCase()} → {flag(p.tgt)} {p.tgt?.toUpperCase()}</span>
              <span style={{ color: "#ffd6a5", fontWeight: 600 }}>{p.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab 6: System Health ──────────────────────────────────────────────────────
function SystemHealth() {
  const [status, setStatus] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [testText, setTestText] = useState("مرحبا، كيف حالك؟");

  useEffect(() => {
    apiFetch("/status").then(setStatus).catch(() => {});
  }, []);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiFetch("/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: testText, source_language: "ar", target_language: "en" }),
      });
      setTestResult(r.result);
    } catch (e: any) {
      setTestResult({ error: e.message });
    } finally {
      setTesting(false);
    }
  };

  const checks = status ? [
    { label: "FFmpeg", ok: status.ffmpeg },
    { label: "OpenAI (Whisper + GPT)", ok: status.openai },
    { label: "ElevenLabs TTS", ok: status.elevenlabs },
    { label: "Redis", ok: status.redis },
    { label: "Group Routing", ok: status.group_routing },
  ] : [];

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif" }}>Component Status</h4>
        {checks.map(c => (
          <div key={c.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13 }}>
            <span style={{ color: "#e2e8f0" }}>{c.label}</span>
            <span style={{ color: c.ok ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{c.ok ? "✅ OK" : "❌ Missing"}</span>
          </div>
        ))}
        {status && (
          <div style={{ marginTop: 10, fontSize: 12, color: status.overall === "ok" ? "#22c55e" : "#eab308", fontWeight: 700 }}>
            Overall: {status.overall?.toUpperCase()}
          </div>
        )}
      </div>

      <div style={card}>
        <h4 style={{ color: "#ffd6a5", margin: "0 0 12px", fontFamily: "'Space Grotesk', sans-serif" }}>Test Pipeline</h4>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            value={testText}
            onChange={e => setTestText(e.target.value)}
            style={{
              flex: 1, padding: "8px 12px", borderRadius: 6,
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)",
              color: "#e2e8f0", fontSize: 13, outline: "none",
            }}
          />
          <button
            onClick={runTest}
            disabled={testing}
            style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: testing ? "#334155" : "#ffd6a5", color: "#0b1326", fontWeight: 700, cursor: testing ? "not-allowed" : "pointer", fontSize: 13 }}
          >
            {testing ? "Running…" : "Run Test"}
          </button>
        </div>
        {testResult && (
          <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 6, padding: 12, fontSize: 12, color: "#e2e8f0" }}>
            {testResult.error ? (
              <span style={{ color: "#ef4444" }}>Error: {testResult.error}</span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div><span style={{ color: "#64748b" }}>Status:</span> <span style={{ color: STATUS_COLOR[testResult.status] || "#e2e8f0" }}>{testResult.status}</span></div>
                <div><span style={{ color: "#64748b" }}>Language:</span> {flag(testResult.detected_language)} {testResult.detected_language?.toUpperCase()}</div>
                <div><span style={{ color: "#64748b" }}>Cleaned:</span> {testResult.cleaned_text || "—"}</div>
                <div><span style={{ color: "#64748b" }}>Translated:</span> {testResult.translated_text || "—"}</div>
                <div><span style={{ color: "#64748b" }}>Pipeline:</span> <span style={{ color: latencyColor(testResult.pipeline_ms) }}>{testResult.pipeline_ms}ms</span></div>
                {testResult.normalized_audio_url && <audio controls src={testResult.normalized_audio_url} style={{ width: "100%", marginTop: 6 }} />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
const TABS = ["Live Feed", "Message Assets", "Groups", "Sessions & Profiles", "Analytics", "System Health"];

const OmniVoiceDashboard: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <div style={{ minHeight: "100vh", background: "#0b1326", padding: "24px 20px", fontFamily: "'Manrope', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg, #ffd6a5, #e7c090)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>🎙️</div>
          <div>
            <h2 style={{ color: "#ffd6a5", margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: 22 }}>OmniVoice v2</h2>
            <div style={{ color: "#64748b", fontSize: 12 }}>Group @mention routing · 4-layer normalization · ElevenLabs TTS</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 20 }}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)} style={tabStyle(tab === i)}>{t}</button>
          ))}
        </div>

        {tab === 0 && <LiveFeed />}
        {tab === 1 && <MessageAssets />}
        {tab === 2 && <Groups />}
        {tab === 3 && <SessionsProfiles />}
        {tab === 4 && <Analytics />}
        {tab === 5 && <SystemHealth />}
      </div>
    </div>
  );
};

export default OmniVoiceDashboard;
